import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export default function AdminGateways() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [drafts, setDrafts] = useState({}); // provider id -> {mode, enabled, values}
  const [open, setOpen] = useState(null);
  const [custom, setCustom] = useState({ name: "", rows: [{ label: "", value: "", secret: true }] });

  const load = () =>
    api.get("/admin/gateways").then(({ data }) => setData(data))
      .catch((err) => { if (err.response?.status === 403) setData({ forbidden: true }); });
  useEffect(() => { load(); }, []);

  const twofa = () => window.prompt("Enter your current 6-digit 2FA code to confirm this payment change:");

  const draftFor = (p) => drafts[p.id] || { mode: p.mode, enabled: p.enabled, values: {} };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...draftFor({ id, mode: "test", enabled: true }), ...d[id], ...patch } }));
  const setVal = (id, key, v) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), values: { ...(d[id]?.values || {}), [key]: v } } }));

  const save = async (p) => {
    const code = twofa(); if (!code) return;
    const dr = draftFor(p);
    setBusy(p.id);
    try {
      await api.put(`/admin/gateways/${p.id}`, { mode: dr.mode, enabled: dr.enabled ?? p.enabled, values: dr.values || {} }, { headers: { "X-2FA-Code": code } });
      toast.success(`${p.name} saved`); setDrafts((d) => ({ ...d, [p.id]: undefined })); setOpen(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  const activate = async (p) => {
    const code = twofa(); if (!code) return;
    setBusy(p.id);
    try { await api.post("/admin/gateways/activate", { provider: p.id }, { headers: { "X-2FA-Code": code } }); toast.success(`${p.name} is now the active gateway`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  const test = async (p) => {
    const dr = draftFor(p); const sk = dr.values?.secret_key;
    if (!sk) return toast.error("Enter a secret key to test");
    setBusy(p.id);
    try { const { data } = await api.post("/admin/gateways/stripe/test", { secret_key: sk }); toast.success(`Connected: ${data.account_id} (${data.mode})`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove the ${p.name} gateway configuration?`)) return;
    try { await api.delete(`/admin/gateways/${p.id}`); toast.success("Gateway removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveCustom = async () => {
    const name = custom.name.trim();
    const rows = custom.rows.filter((r) => r.label.trim());
    if (!name) return toast.error("Give the gateway a name");
    if (!rows.length) return toast.error("Add at least one credential field");
    const code = twofa(); if (!code) return;
    const slug = `custom_${slugify(name)}`;
    const field_meta = rows.map((r) => ({ key: slugify(r.label), label: r.label.trim(), secret: !!r.secret }));
    const values = {}; rows.forEach((r) => { values[slugify(r.label)] = r.value; });
    try {
      await api.put(`/admin/gateways/${slug}`, { name, field_meta, values, enabled: true, mode: "live" }, { headers: { "X-2FA-Code": code } });
      toast.success("Custom gateway added"); setCustom({ name: "", rows: [{ label: "", value: "", secret: true }] }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Payment Gateways" subtitle="Owner-only. Add any provider. Credentials are encrypted at rest and never shown after saving.">
      {!data ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : data.forbidden ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 px-4 py-3 text-sm">Only the Owner can manage payment gateways.</div>
      ) : (
        <div className="space-y-5" data-testid="admin-gateways">
          {data.providers.map((p) => {
            const isActive = data.active === p.id;
            const dr = draftFor(p);
            return (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-5 overflow-hidden" data-testid={`gateway-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name={p.icon || "CreditCard"} className="h-5 w-5 text-primary shrink-0" />
                    <span className="font-bold truncate">{p.name}</span>
                    {isActive && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">active</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{p.enabled ? "enabled" : "disabled"}</span>
                    {p.modes && <span className={`text-xs px-2 py-0.5 rounded-full ${p.mode === "live" ? "bg-red-500/15 text-red-500" : "bg-amber-500/15 text-amber-600"}`}>{p.mode} mode</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isActive && <button data-testid={`gw-activate-${p.id}`} disabled={busy === p.id} onClick={() => activate(p)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">Set active</button>}
                    <button data-testid={`gw-edit-${p.id}`} onClick={() => setOpen(open === p.id ? null : p.id)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">{open === p.id ? "Close" : "Edit"}</button>
                    {p.custom && <button data-testid={`gw-remove-${p.id}`} onClick={() => remove(p)} className="rounded-lg border border-red-500/40 text-red-500 px-2.5 py-1.5 text-xs font-semibold hover:bg-red-500/10"><Icon name="Trash2" className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>

                {/* current config — constrained so long keys never overflow the card */}
                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm min-w-0">
                  {p.fields.map((f) => (
                    <div key={f.key} className="min-w-0">
                      <div className="text-xs text-muted-foreground">{f.label}{f.optional ? " (optional)" : ""}</div>
                      <div className="font-mono text-xs break-all overflow-hidden text-ellipsis">{f.secret ? (f.value_masked || "—") : (f.value || "—")}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Config source: {p.source}</div>

                {open === p.id && (
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {p.modes && (
                        <div>
                          <label className="text-xs font-semibold text-muted-foreground">Mode</label>
                          <select data-testid={`gw-mode-${p.id}`} value={dr.mode} onChange={(e) => setDraft(p.id, { mode: e.target.value })} className="inp"><option value="test">Test</option><option value="live">Live</option></select>
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-sm mt-5">
                        <input type="checkbox" data-testid={`gw-enabled-${p.id}`} checked={dr.enabled ?? p.enabled} onChange={(e) => setDraft(p.id, { enabled: e.target.checked })} /> Enabled
                      </label>
                      {p.fields.map((f) => (
                        <div key={f.key}>
                          <label className="text-xs font-semibold text-muted-foreground">{f.label}{f.optional ? " (optional)" : ""}</label>
                          <input data-testid={`gw-field-${p.id}-${f.key}`} type={f.secret ? "password" : "text"} value={dr.values?.[f.key] ?? (f.secret ? "" : f.value || "")}
                            onChange={(e) => setVal(p.id, f.key, e.target.value)} placeholder={f.placeholder || (f.secret ? "leave blank to keep current" : "")} className="inp" />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {p.testable && <button data-testid={`gw-test-${p.id}`} onClick={() => test(p)} disabled={busy === p.id} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-60">Test connection</button>}
                      <button data-testid={`gw-save-${p.id}`} onClick={() => save(p)} disabled={busy === p.id} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60">Save (2FA)</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add a custom gateway */}
          <div className="rounded-2xl border border-dashed border-border bg-card p-5" data-testid="gateway-custom-add">
            <h3 className="text-sm font-bold mb-1 flex items-center gap-2"><Icon name="Plus" className="h-4 w-4" />Add a custom gateway</h3>
            <p className="text-xs text-muted-foreground mb-3">Use any provider not listed above. Secret fields are encrypted at rest.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Gateway name</label>
                <input data-testid="custom-gw-name" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="e.g. Mollie, Square, PayU" className="inp" />
              </div>
              {custom.rows.map((r, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                  <div><label className="text-xs text-muted-foreground">Field label</label><input value={r.label} onChange={(e) => setCustom({ ...custom, rows: custom.rows.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} placeholder="API key" className="inp" data-testid={`custom-gw-label-${i}`} /></div>
                  <div><label className="text-xs text-muted-foreground">Value</label><input type={r.secret ? "password" : "text"} value={r.value} onChange={(e) => setCustom({ ...custom, rows: custom.rows.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} className="inp" data-testid={`custom-gw-value-${i}`} /></div>
                  <label className="flex items-center gap-1.5 text-xs pb-2.5"><input type="checkbox" checked={r.secret} onChange={(e) => setCustom({ ...custom, rows: custom.rows.map((x, j) => j === i ? { ...x, secret: e.target.checked } : x) })} />Secret</label>
                  <button onClick={() => setCustom({ ...custom, rows: custom.rows.filter((_, j) => j !== i) })} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-accent mb-0.5"><Icon name="X" className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <button onClick={() => setCustom({ ...custom, rows: [...custom.rows, { label: "", value: "", secret: true }] })} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">+ Add field</button>
                <button data-testid="custom-gw-save" onClick={saveCustom} className="rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-bold hover:opacity-90">Save custom gateway (2FA)</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
